import { Helmet } from "react-helmet-async";
import { WebPageJsonLd } from "@/components/seo/WebPageJsonLd";
import Navbar from "@/components/Navbar";
import { canonicalUrl } from "@/lib/app-config";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const MedicalDisclaimer = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Medical Disclaimer | Elara Health</title>
        <meta
          name="description"
          content="Elara Health medical disclaimer: our educational content is not medical advice, diagnosis, or treatment."
        />
        <link rel="canonical" href={canonicalUrl("/medical-disclaimer")} />
        <meta property="og:url" content={canonicalUrl("/medical-disclaimer")} />
      </Helmet>
    <WebPageJsonLd
      path="/medical-disclaimer"
      name={"Medical Disclaimer"}
      description={"Elara Health medical disclaimer: our educational content is not medical advice, diagnosis, or treatment."}
    />
      <Navbar />
      <main className="container mx-auto px-4 py-16 max-w-3xl">
        <h1 className="font-display text-4xl text-foreground mb-8">Medical Disclaimer</h1>

        <div className="prose prose-sm text-muted-foreground space-y-6">
          <section>
            <h2 className="font-display text-xl text-foreground">Not Medical Advice</h2>
            <p>
              The information provided on the Elara Health website and platform is for general informational and educational purposes only. It is not intended as, and should not be construed as, medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions you may have regarding a medical condition.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Provider Independence</h2>
            <p>
              All healthcare providers accessible through the Elara Health platform are independently licensed clinicians. They exercise independent clinical judgment in all treatment decisions, including whether to prescribe medication. Elara Health does not direct, control, or influence any provider's medical decisions. The relationship between you and your provider is a direct patient–provider relationship governed by applicable state and federal law.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">No Guarantee of Treatment</h2>
            <p>
              Completing a health assessment on the Elara Health platform does not guarantee that you will receive a prescription or any particular treatment. Your provider will evaluate your individual health history, current conditions, and clinical data to determine the most appropriate course of action, which may include declining to prescribe.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Compounded Medications</h2>
            <p>
              Some medications available through Elara Health may be compounded by licensed U.S. compounding pharmacies registered with the FDA under Section 503A. Compounded medications are prepared for individual patients based on a provider's prescription and are not FDA-approved in the same manner as commercially manufactured drugs. All partner pharmacies are required to perform endotoxin testing and provide third-party Certificates of Analysis (COA) for compounded products.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Risks and Side Effects</h2>
            <p>
              All medications, including peptide therapies and GLP-1 receptor agonists, carry potential risks and side effects. Your provider will discuss these with you during your consultation. You should not begin or discontinue any treatment without consulting your provider.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Individual Results</h2>
            <p>
              Individual results may vary. Outcomes depend on multiple factors, including adherence to the treatment plan, lifestyle habits, medical history, and biological factors. Testimonials or projected outcomes on the website are not guarantees of results.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Emergency Situations</h2>
            <p>
              Elara Health is not an emergency medical service. If you are experiencing a medical emergency, call 911 or go to your nearest emergency room immediately. Do not use this platform for urgent or emergency medical needs.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground">Contact</h2>
            <p>
              If you have questions about this disclaimer, please visit our{" "}
              <Link to="/contact" className="text-primary hover:underline">Contact page</Link> or review our{" "}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </section>

          <p className="text-xs text-muted-foreground pt-4 border-t border-border">
            Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MedicalDisclaimer;
